import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';
import { environment } from "../../../environments/environment";
import { Subject } from 'rxjs/Subject';
import { Project  } from 'src/app/_models/project.model';
import 'rxjs/add/operator/map';

@Injectable({
  providedIn: 'root'
})
export class ProjectStructureService extends Subject<DataStateChangeEventArgs> {

  constructor(private http: HttpClient) {
    super();
  }

  handleError(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(errorMessage);
  }

  // Get all regions
  getRegion() {
    return this.http.get(`${environment.apiUrl}/regions`).pipe(retry(2), catchError(this.handleError));
  }

  createRegion(details) {
    return this.http.post(`${environment.apiUrl}/regions`, details).pipe(retry(2), catchError(this.handleError));
  }

  updateRegion(id, details) {
    return this.http.put(`${environment.apiUrl}/regions/${id}`, details).pipe(retry(2), catchError(this.handleError));
  }

  // Get all industry
  getIndustry() {
    return this.http.get(`${environment.apiUrl}/industries`).pipe(retry(2), catchError(this.handleError));
  }

  getIndustryByRegion(regionId) {
    return this.http.get(`${environment.apiUrl}/industries?regionId=${regionId}`).pipe(retry(2), catchError(this.handleError));
  }

  createIndustry(details) {
    return this.http.post(`${environment.apiUrl}/industries`, details).pipe(retry(2), catchError(this.handleError));
  }

  updateIndustry(id, details) {
    return this.http.put(`${environment.apiUrl}/industries/${id}`, details).pipe(retry(2), catchError(this.handleError));
  }

  // Get all BusinessGroup
  getBusinessGroup() {
    return this.http.get(`${environment.apiUrl}/businessGroups`).pipe(retry(2), catchError(this.handleError));
  }

  getBusinessGroupByIndustry(industryId) {
    return this.http.get(`${environment.apiUrl}/businessGroups?industryId=${industryId}`).pipe(retry(2), catchError(this.handleError));
  }

  createBusinessGroup(details) {
    return this.http.post(`${environment.apiUrl}/businessGroups`, details).pipe(retry(2), catchError(this.handleError));
  }

  updateBusinessGroup(id, details) {
    return this.http.put(`${environment.apiUrl}/businessGroups/${id}`, details).pipe(retry(2), catchError(this.handleError));
  }

  // Get all Commodity
  getCommodity() {
    return this.http.get(`${environment.apiUrl}/commodities`).pipe(retry(2), catchError(this.handleError));
  }

  getCommodityByBusinessGroup(businessGroupId) {
    return this.http.get(`${environment.apiUrl}/commodities?businessGroupId=${businessGroupId}`).pipe(retry(2), catchError(this.handleError));
  }

  createCommodity(details) {
    return this.http.post(`${environment.apiUrl}/commodities`, details).pipe(retry(2), catchError(this.handleError));
  }

  updateCommodity(id, details) {
    return this.http.put(`${environment.apiUrl}/commodities/${id}`, details).pipe(retry(2), catchError(this.handleError));
  }

  // Get all Activity
  getActivity() {
    return this.http.get(`${environment.apiUrl}/activities`).pipe(retry(2), catchError(this.handleError));
  }

  createActivity(details) {
    return this.http.post(`${environment.apiUrl}/activities`, details).pipe(retry(2), catchError(this.handleError));
  }

  updateActivity(id, details) {
    return this.http.put(`${environment.apiUrl}/activities/${id}`, details).pipe(retry(2), catchError(this.handleError));
  }

  // Get all ProjectSoftware
  getProjectSoftware() {
    return this.http.get(`${environment.apiUrl}/projectSoftwares`).pipe(retry(2), catchError(this.handleError));
  }

  getProjectSoftwareByActivity(activityId) {
    return this.http.get(`${environment.apiUrl}/projectSoftwares?activityId=${activityId}`).pipe(retry(2), catchError(this.handleError));
  }

  createProjectSoftware(details) {
    return this.http.post(`${environment.apiUrl}/projectSoftwares`, details).pipe(retry(2), catchError(this.handleError));
  }

  updateProjectSoftware(id, details) {
    return this.http.put(`${environment.apiUrl}/projectSoftwares/${id}`, details).pipe(retry(2), catchError(this.handleError));
  }

  // Get all ProjectSoftware
  getTaskDropdown() {
    return this.http.get(`${environment.apiUrl}/taskDropdowns`).pipe(retry(2), catchError(this.handleError));
  }

  getTaskDropdownByActivity(activityId) {
    return this.http.get(`${environment.apiUrl}/taskDropdowns?activityId=${activityId}`).pipe(retry(2), catchError(this.handleError));
  }

  createTaskDropdown(details) {
    return this.http.post(`${environment.apiUrl}/taskDropdowns`, details).pipe(retry(2), catchError(this.handleError));
  }

  updateTaskDropdown(id, details) {
    return this.http.put(`${environment.apiUrl}/taskDropdowns/${id}`, details).pipe(retry(2), catchError(this.handleError));
  }

  deleteRegion(id) {
    return this.http.delete(`${environment.apiUrl}/regions/${id}`).pipe(retry(2), catchError(this.handleError));
  }

  deleteIndustry(id) {
    return this.http.delete(`${environment.apiUrl}/industries/${id}`).pipe(retry(2), catchError(this.handleError));
  }

  deleteBusinessGroup(id) {
    return this.http.delete(`${environment.apiUrl}/businessGroups/${id}`).pipe(retry(2), catchError(this.handleError));
  }

  deleteCommodity(id) {
    return this.http.delete(`${environment.apiUrl}/commodities/${id}`).pipe(retry(2), catchError(this.handleError));
  }

  deleteActivity(id) {
    return this.http.delete(`${environment.apiUrl}/activities/${id}`).pipe(retry(2), catchError(this.handleError));
  }

  deleteProjectSoftware(id) {
    return this.http.delete(`${environment.apiUrl}/projectSoftwares/${id}`).pipe(retry(2), catchError(this.handleError));
  }

  deleteTaskDropdown(id) {
    return this.http.delete(`${environment.apiUrl}/taskDropdowns/${id}`).pipe(retry(2), catchError(this.handleError));
  }

  getAllProjectsV2() {
    return this.http.get(`${environment.apiUrl}/projectV2`).pipe(retry(2), catchError(this.handleError));
  }

  deleteProjectV2(id) {
    return this.http.delete(`${environment.apiUrl}/projectV2/${id}`).pipe(retry(2), catchError(this.handleError));
  }

}
